import { useContext, useEffect, useMemo, useState } from 'react';
import {
    Modal, ModalDialog, ModalClose,
    Box, Stack, Typography, Chip,
    Input, Checkbox, Button, CircularProgress,
    Avatar,
} from '@mui/joy';

import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';

import { AppContext } from '../../context';
import {
    APIGetEmployees,
    APIAssignRole,
    APIUnassignRole,
} from '../../api';

const getInitials = (acc) => {
    if (!acc) return '?';
    const f = acc.first_name?.[0] || '';
    const l = acc.last_name?.[0] || '';
    return (f + l).toUpperCase() || acc.email?.[0]?.toUpperCase() || '?';
};

const ROLE_LABEL = {
    manager:            'Руководитель',
    photographer:       'Фотограф',
    admin:              'Админ точки',
    admin_photographer: 'Админ + Фотограф',
    workstation:        'Рабочий аккаунт',
};

const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    color: 'var(--joy-palette-text-primary)',
    fontSize: 13,
    '&:hover, &:focus-within': {
        borderColor: 'var(--joy-palette-primary-500)',
        bgcolor: 'var(--joy-palette-background-level2)',
    },
};

/**
 * Модалка массового назначения / снятия роли.
 *
 * Props:
 *   open: boolean
 *   role: { id, name, accounts_count?, permissions_count? }
 *   onClose: () => void
 *   onSaved: () => void   — вызывается после успешного применения
 */
export default function AssignRoleModal({ open, role, onClose, onSaved }) {
    const { token, showToast } = useContext(AppContext);

    // ── Данные ────────────────────────────────────────────────
    const [employees, setEmployees] = useState([]);
    const [loading,   setLoading]   = useState(false);
    const [saving,    setSaving]    = useState(false);

    // ── Фильтры ──────────────────────────────────────────────
    const [search, setSearch]       = useState('');
    const [showOnlyAssigned, setShowOnlyAssigned] = useState(false);

    // ── Изменения ────────────────────────────────────────────
    // initialAssigned — кто УЖЕ имеет эту роль (id'ы)
    // currentSelected — кто отмечен в чекбоксе сейчас
    const [initialAssigned, setInitialAssigned] = useState(new Set());
    const [currentSelected, setCurrentSelected] = useState(new Set());

    // ── Загрузка списка при открытии ─────────────────────────
    useEffect(() => {
        if (!open || !role || !token) return;

        let cancelled = false;
        (async () => {
            setLoading(true);
            const data = await APIGetEmployees(token);
            if (cancelled) return;

            if (Array.isArray(data)) {
                // Фильтруем: только сотрудники (workstation тоже могут иметь
                // custom_role, но обычно роль им не назначают — оставим всех)
                const list = data.filter(e => !e.is_blocked && !e.is_fired);
                setEmployees(list);

                const assigned = new Set(
                    list
                        .filter(e => e.custom_role === role.id || e.custom_role?.id === role.id)
                        .map(e => e.id),
                );
                setInitialAssigned(assigned);
                setCurrentSelected(new Set(assigned));
            }
            setLoading(false);
        })();

        return () => { cancelled = true; };
    }, [open, role, token]);

    // ── Поиск ─────────────────────────────────────────────────
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = employees;
        if (showOnlyAssigned) {
            list = list.filter(e => currentSelected.has(e.id));
        }
        if (q) {
            list = list.filter(e => {
                const full = `${e.first_name || ''} ${e.last_name || ''} ${e.email || ''}`.toLowerCase();
                return full.includes(q);
            });
        }
        return list;
    }, [employees, search, showOnlyAssigned, currentSelected]);

    // ── Что изменилось ────────────────────────────────────────
    const { toAdd, toRemove } = useMemo(() => {
        const add = [];
        const remove = [];
        currentSelected.forEach(id => {
            if (!initialAssigned.has(id)) add.push(id);
        });
        initialAssigned.forEach(id => {
            if (!currentSelected.has(id)) remove.push(id);
        });
        return { toAdd: add, toRemove: remove };
    }, [initialAssigned, currentSelected]);

    const hasChanges = toAdd.length > 0 || toRemove.length > 0;

    // ── Переключение чекбокса ────────────────────────────────
    const toggle = (employeeId) => {
        setCurrentSelected(prev => {
            const next = new Set(prev);
            if (next.has(employeeId)) next.delete(employeeId);
            else next.add(employeeId);
            return next;
        });
    };

    // ── Сохранение ───────────────────────────────────────────
    const handleSave = async () => {
        if (!hasChanges || !role) return;
        setSaving(true);

        let assignedOk = true;
        let unassignedOk = true;

        if (toAdd.length > 0) {
            const res = await APIAssignRole(token, role.id, toAdd);
            if (res?.error) {
                showToast('Ошибка при назначении роли', 'danger');
                assignedOk = false;
            }
        }
        if (toRemove.length > 0) {
            const res = await APIUnassignRole(token, role.id, toRemove);
            if (res?.error) {
                showToast('Ошибка при снятии роли', 'danger');
                unassignedOk = false;
            }
        }

        setSaving(false);

        if (assignedOk && unassignedOk) {
            const parts = [];
            if (toAdd.length)    parts.push(`назначена ${toAdd.length}`);
            if (toRemove.length) parts.push(`снята с ${toRemove.length}`);
            showToast(`Роль «${role.name}» ${parts.join(', ')}`);
            if (onSaved) onSaved();
        }
    };

    if (!role) return null;

    return (
        <Modal
            open={open}
            onClose={onClose}
            slotProps={{
                backdrop: {
                    sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' },
                },
            }}
            sx={{ touchAction: 'none', overscrollBehavior: 'contain' }}
        >
            <ModalDialog
                sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                    maxWidth: 520, width: '100%',
                    p: 3,
                    maxHeight: { xs: '100dvh', sm: '90vh' },
                    height:    { xs: '100dvh', sm: 'auto' },
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                    display: 'flex', flexDirection: 'column',
                }}
            >
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                {/* ── Шапка ──────────────────────────────────────── */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <SecurityRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', letterSpacing: '-0.01em' }}>
                            Назначить роль «{role.name}»
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                        Отметьте, у каких сотрудников должна быть эта роль.
                        Снятие галочки = роль будет снята.
                    </Typography>
                </Box>

                {/* ── Поиск ─────────────────────────────────────── */}
                <Box sx={{ mb: 1.5, display: 'flex', gap: 1 }}>
                    <Input
                        placeholder="Поиск по имени или email…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        startDecorator={<SearchRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-text-tertiary)' }} />}
                        size="sm"
                        sx={{ ...inputSx, flex: 1 }}
                    />
                    <Chip
                        size="sm" variant={showOnlyAssigned ? 'solid' : 'outlined'}
                        color={showOnlyAssigned ? 'primary' : 'neutral'}
                        onClick={() => setShowOnlyAssigned(v => !v)}
                        sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 11, px: 1.5 }}
                    >
                        {showOnlyAssigned ? 'Только с ролью' : 'Все'}
                    </Chip>
                </Box>

                {/* ── Список ─────────────────────────────────────── */}
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mb: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size="md" />
                        </Box>
                    ) : filtered.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                Никого не нашлось
                            </Typography>
                        </Box>
                    ) : (
                        <Stack spacing={0.6}>
                            {filtered.map(emp => {
                                const checked = currentSelected.has(emp.id);
                                const wasAssigned = initialAssigned.has(emp.id);
                                return (
                                    <Box
                                        key={emp.id}
                                        onClick={() => toggle(emp.id)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.2,
                                            px: 1.2, py: 1,
                                            borderRadius: 'var(--joy-radius-md)',
                                            border: '1px solid var(--joy-palette-divider)',
                                            bgcolor: checked
                                                ? 'rgba(162,38,226,0.06)'
                                                : 'var(--joy-palette-background-level2)',
                                            cursor: 'pointer',
                                            transition: 'all .15s',
                                            '&:hover': { borderColor: 'rgba(162,38,226,0.4)' },
                                        }}
                                    >
                                        <Checkbox
                                            checked={checked}
                                            onChange={() => toggle(emp.id)}
                                            size="sm"
                                        />
                                        <Avatar sx={{
                                            width: 32, height: 32, fontSize: 11, fontWeight: 700,
                                            bgcolor: 'rgba(162,38,226,0.12)',
                                            color: 'var(--joy-palette-primary-300)',
                                            border: '1px solid var(--joy-palette-divider)',
                                        }}>
                                            {getInitials(emp)}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                    {emp.full_name || `${emp.first_name} ${emp.last_name}`.trim() || emp.email}
                                                </Typography>
                                                {wasAssigned && (
                                                    <Chip size="sm" sx={{
                                                        bgcolor: 'rgba(34,197,94,0.1)', color: '#22c55e',
                                                        fontSize: 9, fontWeight: 700, border: 'none',
                                                    }}>
                                                        Уже назначена
                                                    </Chip>
                                                )}
                                            </Box>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                {ROLE_LABEL[emp.role] || emp.role} · {emp.email}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Box>

                {/* ── Сводка изменений ────────────────────────────── */}
                {hasChanges && (
                    <Box sx={{
                        mb: 2, p: 1.2,
                        borderRadius: 'var(--joy-radius-md)',
                        border: '1px solid rgba(162,38,226,0.2)',
                        bgcolor: 'rgba(162,38,226,0.04)',
                    }}>
                        <Stack spacing={0.4}>
                            {toAdd.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                    <GroupAddRoundedIcon sx={{ fontSize: 13, color: '#22c55e' }} />
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)' }}>
                                        Назначить: <b style={{ color: '#22c55e' }}>{toAdd.length}</b>
                                    </Typography>
                                </Box>
                            )}
                            {toRemove.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                    <PersonRemoveRoundedIcon sx={{ fontSize: 13, color: '#f87171' }} />
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)' }}>
                                        Снять: <b style={{ color: '#f87171' }}>{toRemove.length}</b>
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                )}

                {/* ── Кнопки ───────────────────────────────────────── */}
                <Box sx={{ display: 'flex', gap: 1.2 }}>
                    <Button
                        variant="outlined" color="neutral"
                        onClick={onClose}
                        disabled={saving}
                        sx={{ flex: 1, fontWeight: 700 }}
                    >
                        Отмена
                    </Button>
                    <Button
                        variant="solid" color="primary"
                        onClick={handleSave}
                        loading={saving}
                        disabled={!hasChanges || saving}
                        sx={{ flex: 1, fontWeight: 700 }}
                    >
                        Применить
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}